import {
  Box,
  Button,
  IconButton,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import LabeledBox from '../../special/LabeledBox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

const moment = require('moment');

const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const TypeTimeWindows = ({
  field,
  dataCollection,
  updateValidationOnChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const timeWindows = dataCollection[field.key] || {};

  function updateWindows(newWindows) {
    updateValidationOnChange(field.key, newWindows);
  }

  function handleToggleDay(day) {
    const newWindows = { ...timeWindows };
    if (newWindows[day] === null || newWindows[day] === undefined) {
      newWindows[day] = [{ start: '08:00', end: '17:00' }];
    } else {
      newWindows[day] = null;
    }
    updateWindows(newWindows);
  }

  function handleAddWindow(day) {
    const newWindows = { ...timeWindows };
    const dayWindows = [...(newWindows[day] || [])];
    dayWindows.push({ start: '08:00', end: '17:00' });
    newWindows[day] = dayWindows;
    updateWindows(newWindows);
  }

  function handleRemoveWindow(day, index) {
    const newWindows = { ...timeWindows };
    const dayWindows = [...(newWindows[day] || [])];
    dayWindows.splice(index, 1);
    newWindows[day] = dayWindows.length > 0 ? dayWindows : null;
    updateWindows(newWindows);
  }

  function handleTimeChange(day, index, key, momentValue) {
    if (!momentValue || !momentValue.isValid()) return;
    const newWindows = { ...timeWindows };
    const dayWindows = [...(newWindows[day] || [])];
    dayWindows[index] = {
      ...dayWindows[index],
      [key]: momentValue.format('HH:mm'),
    };
    newWindows[day] = dayWindows;
    updateWindows(newWindows);
  }

  const dayLabels = {
    monday: t('Monday'),
    tuesday: t('Tuesday'),
    wednesday: t('Wednesday'),
    thursday: t('Thursday'),
    friday: t('Friday'),
    saturday: t('Saturday'),
    sunday: t('Sunday'),
  };

  return (
    <LabeledBox
      label={field.label}
      sx={{ padding: '10px 0px', cursor: 'unset!important' }}
    >
      <Box className="flex flex-col gap-1">
        {WEEKDAYS.map((day) => {
          const isActive =
            timeWindows[day] !== null && timeWindows[day] !== undefined;
          const windows = timeWindows[day] || [];

          return (
            <Box
              key={day}
              className="flex items-start gap-2 py-1"
              sx={{
                borderBottom: `1px solid ${colors.selected}`,
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box className="flex items-center gap-1" sx={{ minWidth: '140px' }}>
                <Switch
                  checked={isActive}
                  onChange={() => handleToggleDay(day)}
                  size="small"
                />
                <Typography
                  variant="body2"
                  sx={{
                    opacity: isActive ? 1 : 0.4,
                    fontWeight: isActive ? 500 : 400,
                  }}
                >
                  {dayLabels[day]}
                </Typography>
              </Box>

              {isActive ? (
                <Box className="flex flex-col gap-1 flex-1">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    {windows.map((window, index) => (
                      <Box
                        key={index}
                        className="flex items-center gap-2"
                      >
                        <TimePicker
                          value={moment(window.start, 'HH:mm')}
                          onChange={(val) =>
                            handleTimeChange(day, index, 'start', val)
                          }
                          ampm={false}
                          slotProps={{
                            textField: { size: 'small', sx: { width: '120px' } },
                          }}
                        />
                        <Typography variant="body2">–</Typography>
                        <TimePicker
                          value={moment(window.end, 'HH:mm')}
                          onChange={(val) =>
                            handleTimeChange(day, index, 'end', val)
                          }
                          ampm={false}
                          slotProps={{
                            textField: { size: 'small', sx: { width: '120px' } },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveWindow(day, index)}
                        >
                          <RemoveCircleIcon color="error" fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </LocalizationProvider>
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => handleAddWindow(day)}
                    sx={{ alignSelf: 'flex-start', paddingLeft: '2px' }}
                  >
                    <AddCircleIcon
                      color="success"
                      fontSize="small"
                      sx={{ mr: 0.5 }}
                    />
                    {t('Add')}
                  </Button>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.4, pt: '6px' }}
                >
                  {t('Closed')}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </LabeledBox>
  );
};

export default TypeTimeWindows;
